import { create } from 'zustand';
import { PublicCourse, SearchFilters, SortOption } from '@/types/course';
import { CourseApi } from '@/services/courseApi';

interface SearchFiltersState {
  searchQuery: string;
  courseTypes: string[];
  durationRanges: string[];
  sortBy: SortOption;
  sortDirection: 'asc' | 'desc';
}

interface CourseStore {
  // Course data
  courses: PublicCourse[];
  selectedCourse: PublicCourse | null;
  featuredCourses: PublicCourse[];
  
  // Search and filtering
  searchFilters: SearchFiltersState;
  
  // UI state
  loading: boolean;
  error: string | null;
  
  // Pagination
  currentPage: number;
  totalPages: number;
  totalCourses: number;
  
  // Actions
  fetchCourses: (filters?: SearchFilters) => Promise<void>;
  searchCourses: (filters: SearchFiltersState) => Promise<void>;
  fetchCourseDetail: (id: string) => Promise<void>;
  fetchFeaturedCourses: () => Promise<void>;
  updateSearchFilters: (filters: Partial<SearchFiltersState>) => void;
  loadPage: (page: number) => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPrevPage: () => Promise<void>;
  clearError: () => void;
  resetSearch: () => void;
}

const defaultFilters: SearchFiltersState = {
  searchQuery: '',
  courseTypes: [],
  durationRanges: [],
  sortBy: SortOption.ALPHABETICAL,
  sortDirection: 'asc',
};

export const useCourseStore = create<CourseStore>((set, get) => ({
  // Initial state
  courses: [],
  selectedCourse: null,
  featuredCourses: [],
  searchFilters: defaultFilters,
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalCourses: 0,
  
  // Actions
  fetchCourses: async (filters?: SearchFilters) => {
    set({ loading: true, error: null });
    
    try {
      const result = await CourseApi.getCourses(filters);
      set({ 
        courses: result.courses, 
        loading: false,
        currentPage: result.pagination.currentPage,
        totalPages: result.pagination.totalPages,
        totalCourses: result.pagination.totalCourses
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch courses',
        loading: false,
        courses: [],
        currentPage: 1,
        totalPages: 1,
        totalCourses: 0
      });
    }
  },
  
  searchCourses: async (filters: SearchFiltersState) => {
    set({ loading: true, error: null, searchFilters: filters });
    
    try {
      const searchFilters: SearchFilters = {
        search: filters.searchQuery || undefined,
        method: filters.courseTypes.length > 0 ? filters.courseTypes as any : undefined,
        duration: filters.durationRanges.length > 0 ? filters.durationRanges as any : undefined,
        sort: filters.sortBy,
        sortDirection: filters.sortDirection,
      };
      
      const result = await CourseApi.getCourses(searchFilters);
      set({ 
        courses: result.courses, 
        loading: false,
        currentPage: result.pagination.currentPage,
        totalPages: result.pagination.totalPages,
        totalCourses: result.pagination.totalCourses
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to search courses',
        loading: false,
        courses: [],
        currentPage: 1,
        totalPages: 1,
        totalCourses: 0
      });
    }
  },
  
  fetchCourseDetail: async (id: string) => {
    set({ loading: true, error: null });
    
    try {
      const course = await CourseApi.getCourse(id);
      set({ 
        selectedCourse: course, 
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch course details',
        loading: false,
        selectedCourse: null
      });
    }
  },
  
  fetchFeaturedCourses: async () => {
    set({ loading: true, error: null });
    
    try {
      const featuredCourses = await CourseApi.getFeaturedCourses();
      set({ 
        featuredCourses, 
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch featured courses',
        loading: false,
        featuredCourses: []
      });
    }
  },
  
  updateSearchFilters: (filters: Partial<SearchFiltersState>) => {
    const currentFilters = get().searchFilters;
    set({ 
      searchFilters: { 
        ...currentFilters, 
        ...filters 
      } 
    });
  },

  loadPage: async (page: number) => {
    const { searchFilters } = get();
    const apiFilters: SearchFilters = {
      search: searchFilters.searchQuery || undefined,
      method: searchFilters.courseTypes.length > 0 ? searchFilters.courseTypes as any : undefined,
      duration: searchFilters.durationRanges.length > 0 ? searchFilters.durationRanges as any : undefined,
      sort: searchFilters.sortBy,
      sortDirection: searchFilters.sortDirection,
      page,
    };
    
    await get().fetchCourses(apiFilters);
  },

  loadNextPage: async () => {
    const { currentPage, totalPages } = get();
    if (currentPage < totalPages) {
      await get().loadPage(currentPage + 1);
    }
  },

  loadPrevPage: async () => {
    const { currentPage } = get();
    if (currentPage > 1) {
      await get().loadPage(currentPage - 1);
    }
  },
  
  clearError: () => {
    set({ error: null });
  },
  
  resetSearch: () => {
    set({ 
      searchFilters: defaultFilters,
      courses: [],
      currentPage: 1,
      totalPages: 1,
      totalCourses: 0
    });
  },
}));